// File: components/MusicPlayer.tsx
import React from 'react';
import { Play, Pause, SkipBack, SkipForward, Music, Volume2, VolumeX, Shuffle, Radio } from 'lucide-react';
import { Song } from './types/common';
import { IdolRank } from '../types';
import { MusicMixMode } from './audio/GameAudioManager';

interface MusicPlayerProps {
    currentSong: Song;
    isPlaying: boolean;
    isMuted: boolean;
    isShuffle?: boolean;
    playerRank: IdolRank;
    mixMode: MusicMixMode;
    onTogglePlay: () => void;
    onToggleMute: () => void;
    onToggleShuffle?: () => void;
    onToggleMixMode?: () => void;
    onNext: () => void;
    onPrev: () => void;
    className?: string;
}

export const MusicPlayer: React.FC<MusicPlayerProps> = ({
    currentSong,
    isPlaying,
    isMuted,
    isShuffle = false,
    playerRank,
    mixMode,
    onTogglePlay,
    onToggleMute,
    onToggleShuffle,
    onToggleMixMode,
    onNext,
    onPrev,
    className = ""
}) => {
    // Resonance Selector Unlocked at Rank 3 (SUPERSTAR)
    const isMixUnlocked = (playerRank || 0) >= IdolRank.SUPERSTAR;

    return (
        <div className={`relative flex items-center gap-2 bg-slate-900/80 border border-slate-700/50 p-2 rounded-lg backdrop-blur-md ${className}`}>
            {/* Song Info (Compact) */}
            <div className="flex items-center gap-2 mr-2 min-w-0 max-w-[120px] md:max-w-[200px]">
                <div className={`shrink-0 p-1.5 rounded-md transition-all duration-500 ${isPlaying ? 'bg-pink-500/20 text-pink-400' : 'bg-slate-800 text-slate-500'}`}>
                    <Music size={12} className={isPlaying ? 'animate-pulse' : ''} />
                </div>
                <div className="flex flex-col min-w-0 overflow-hidden">
                    <span className="text-[10px] font-heading font-black text-white truncate uppercase tracking-tight" title={currentSong?.title || 'No Song Loaded'}>
                        {currentSong?.title || 'No Song Loaded'}
                    </span>
                </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1">
                <button onClick={onPrev} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors">
                    <SkipBack size={14} fill="currentColor" />
                </button>

                <button onClick={onTogglePlay} className="p-2 bg-pink-600 hover:bg-pink-500 text-white rounded-full transition-all shadow-sm active:scale-95 mx-1">
                    {isPlaying ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" className="ml-0.5" />}
                </button>

                <button onClick={onNext} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors">
                    <SkipForward size={14} fill="currentColor" />
                </button>

                <div className="w-px h-4 bg-slate-700 mx-1 hidden sm:block"></div>

                {isMixUnlocked && onToggleMixMode && (
                    <button
                        onClick={onToggleMixMode}
                        className={`group relative flex items-center gap-1.5 px-2 py-1 rounded transition-all border ${mixMode === 'SEQUENCE' ? 'bg-purple-600/30 border-purple-400 text-purple-300' :
                            mixMode === 'MIDI' ? 'bg-amber-600/30 border-amber-400 text-amber-300' :
                                mixMode === 'MP3' ? 'bg-blue-600/30 border-blue-400 text-blue-300' :
                                    'bg-slate-800 border-slate-600 text-slate-400 hover:text-white'
                            }`}
                        title="Cycle Resonance Mode"
                    >
                        <Radio size={14} className={isPlaying ? 'animate-pulse' : ''} />
                        <span className="text-[9px] font-black tracking-widest">
                            {mixMode === 'MIDI' ? 'MID' : mixMode === 'SEQUENCE' ? 'SEQ' : 'MP3'}
                        </span>
                    </button>
                )}

                {onToggleShuffle && (
                    <button
                        onClick={onToggleShuffle}
                        className={`p-1.5 rounded transition-colors hidden sm:block ${isShuffle ? 'text-pink-400 bg-pink-500/10' : 'text-slate-500 hover:text-white'}`}
                    >
                        <Shuffle size={14} />
                    </button>
                )}

                <button
                    onClick={onToggleMute}
                    className={`p-1.5 rounded transition-colors ${isMuted ? 'text-red-500 bg-red-500/10' : 'text-slate-500 hover:text-white'}`}
                >
                    {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                </button>
            </div>
        </div>
    );
};